import React from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Stack,
  Divider,
  Chip,
  Tooltip,
  SvgIcon,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  WhatsApp as WhatsAppIcon,
  IosShare as IosShareIcon,
  Link as LinkIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  MailOutline as MailOutlineIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';

export type ShareDialogProps = {
  open: boolean;
  onClose: () => void;
  url?: string;
  id?: string;
  title?: string;
};

const copyText = async (text: string): Promise<boolean> => {
  const value = String(text || '').trim();
  if (!value) return false;

  try {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // ignore
  }

  try {
    const ta = document.createElement('textarea');
    ta.value = value;
    ta.setAttribute('readonly', 'true');
    ta.style.position = 'fixed';
    ta.style.top = '-1000px';
    ta.style.left = '-1000px';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
};

const buildWhatsAppUrl = (text: string) => {
  const msg = encodeURIComponent(String(text || '').trim());
  return `https://wa.me/?text=${msg}`;
};

const buildFacebookShareUrl = (url: string) => {
  const u = encodeURIComponent(String(url || '').trim());
  return `https://www.facebook.com/sharer/sharer.php?u=${u}`;
};

const buildGmailComposeUrl = (subject: string, body: string) => {
  const s = encodeURIComponent(String(subject || '').trim());
  const b = encodeURIComponent(String(body || '').trim());
  return `mailto:?subject=${s}&body=${b}`;
};

const TikTokIcon: React.FC<{ fontSize?: 'inherit' | 'small' | 'medium' | 'large' }> = ({ fontSize }) => {
  return (
    <SvgIcon fontSize={fontSize} viewBox="0 0 24 24">
      <path d="M16.6 5.82c.57.55 1.26.94 2.02 1.13v2.16c-1.26-.08-2.47-.56-3.43-1.38v6.74c0 3.07-2.49 5.56-5.56 5.56S4.07 17.54 4.07 14.47c0-3.07 2.49-5.56 5.56-5.56.2 0 .39.01.58.03v2.21c-.19-.04-.38-.06-.58-.06-1.84 0-3.34 1.49-3.34 3.34 0 1.84 1.49 3.34 3.34 3.34 1.84 0 3.34-1.49 3.34-3.34V3h2.07c.12 1.07.62 2.05 1.42 2.82Z" />
    </SvgIcon>
  );
};

const ShareDialog: React.FC<ShareDialogProps> = ({ open, onClose, url, id, title }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const shareUrl = String(url || '').trim();
  const fallbackId = String(id || '').trim();
  const linkValue = shareUrl;
  const idValue = fallbackId;
  const primaryValue = linkValue || idValue;

  const shareText = React.useMemo(() => {
    const t = String(title || '').trim();
    if (t && primaryValue) return `${t}: ${primaryValue}`;
    if (primaryValue) return primaryValue;
    return '';
  }, [primaryValue, title]);

  const canNativeShare = Boolean((navigator as any)?.share && primaryValue);

  const handleCopyLink = async () => {
    const ok = await copyText(linkValue);
    if (ok) toast.success('Link copied');
    else toast.error('Copy failed');
  };

  const handleCopyId = async () => {
    const ok = await copyText(idValue);
    if (ok) toast.success('ID copied');
    else toast.error('Copy failed');
  };

  const handleWhatsApp = () => {
    if (!primaryValue) return;
    window.open(buildWhatsAppUrl(shareText), '_blank', 'noopener,noreferrer');
  };

  const handleGmail = () => {
    if (!primaryValue) return;
    const subj = String(title || 'Shared link').trim() || 'Shared link';
    window.location.href = buildGmailComposeUrl(subj, shareText);
  };

  const handleFacebook = () => {
    if (!linkValue) {
      toast.error('No link available');
      return;
    }
    window.open(buildFacebookShareUrl(linkValue), '_blank', 'noopener,noreferrer');
  };

  const handleInstagram = async () => {
    if (!primaryValue) return;
    if (canNativeShare) {
      await handleNativeShare();
      toast.success('Choose Instagram from the share sheet');
      return;
    }

    if (linkValue) await handleCopyLink().catch(() => null);
    else await handleCopyId().catch(() => null);
    toast.success(linkValue ? 'Link copied — open Instagram and paste it' : 'ID copied — open Instagram and paste it');
  };

  const handleTikTok = async () => {
    if (!primaryValue) return;
    if (canNativeShare) {
      await handleNativeShare();
      toast.success('Choose TikTok from the share sheet');
      return;
    }

    if (linkValue) await handleCopyLink().catch(() => null);
    else await handleCopyId().catch(() => null);
    toast.success(linkValue ? 'Link copied — open TikTok and paste it' : 'ID copied — open TikTok and paste it');
  };

  const handleNativeShare = async () => {
    if (!canNativeShare) return;
    try {
      await (navigator as any).share({
        title: String(title || 'Share'),
        text: shareText,
        url: shareUrl || undefined,
      });
    } catch {
      // ignore
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          border: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 22px 60px rgba(2,132,199,0.20)',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.94) 100%)',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
        >
          <Box
            sx={{
              p: 2,
              px: 2.25,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid rgba(0,0,0,0.06)',
              background:
                'radial-gradient(900px 240px at 10% 10%, rgba(99,102,241,0.18), transparent), radial-gradient(700px 220px at 90% 0%, rgba(14,165,233,0.16), transparent), linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(248,250,252,0.92) 100%)',
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 900, lineHeight: 1.1 }} noWrap>
                Teilen
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                Link kopieren oder direkt versenden
              </Typography>
            </Box>
            <IconButton onClick={onClose} aria-label="close" sx={{ bgcolor: 'rgba(255,255,255,0.8)', border: '1px solid rgba(0,0,0,0.06)' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 2.25 }}>
            <Box
              sx={{
                p: 1.6,
                borderRadius: 3,
                border: '1px solid rgba(0,0,0,0.06)',
                bgcolor: 'rgba(255,255,255,0.72)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 900, lineHeight: 1.15 }} noWrap>
                    {String(title || 'Product').trim() || 'Product'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                    {idValue ? <Chip size="small" label={`ID: ${idValue}`} sx={{ fontWeight: 800 }} /> : null}
                    {linkValue ? <Chip size="small" label="Link available" sx={{ fontWeight: 800 }} /> : <Chip size="small" label="No link" sx={{ fontWeight: 800 }} />}
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ my: 1.5 }} />

              <Stack spacing={1.25}>
                <TextField
                  value={linkValue}
                  fullWidth
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <LinkIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={linkValue ? 'Copy link' : 'No link available'}>
                          <span>
                            <IconButton size="small" onClick={handleCopyLink} disabled={!linkValue} aria-label="copy link">
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  placeholder={idValue ? `Product link (fallback ID: ${idValue})` : 'No share link available'}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      bgcolor: 'rgba(255,255,255,0.85)',
                      backdropFilter: 'blur(8px)',
                    },
                  }}
                />

                <TextField
                  value={idValue}
                  fullWidth
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 900 }}>
                          #
                        </Typography>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={idValue ? 'Copy ID' : 'No ID available'}>
                          <span>
                            <IconButton size="small" onClick={handleCopyId} disabled={!idValue} aria-label="copy id">
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  placeholder="No ID available"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      bgcolor: 'rgba(255,255,255,0.85)',
                      backdropFilter: 'blur(8px)',
                    },
                  }}
                />
              </Stack>
            </Box>

            <Stack direction={isMobile ? 'column' : 'row'} spacing={1.25} sx={{ mt: 1.5 }}>
              <Button
                onClick={handleCopyLink}
                disabled={!linkValue}
                variant="contained"
                startIcon={<ContentCopyIcon />}
                fullWidth
                sx={{
                  borderRadius: 3,
                  py: 1.1,
                  fontWeight: 900,
                  background: 'linear-gradient(135deg, rgba(99,102,241,1) 0%, rgba(14,165,233,1) 100%)',
                  boxShadow: '0 14px 32px rgba(99,102,241,0.22)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, rgba(99,102,241,1) 0%, rgba(16,185,129,1) 120%)',
                    boxShadow: '0 16px 40px rgba(99,102,241,0.28)',
                  },
                }}
              >
                Copy link
              </Button>

              <Button
                onClick={handleCopyId}
                disabled={!idValue}
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                fullWidth
                sx={{ borderRadius: 3, py: 1.1, fontWeight: 900 }}
              >
                Copy ID
              </Button>
            </Stack>

            <Box sx={{ mt: 1.75 }}>
              <Typography variant="body2" sx={{ fontWeight: 900, mb: 1 }}>
                Share via
              </Typography>
              <Stack direction={isMobile ? 'column' : 'row'} spacing={1}>
                <Button
                  onClick={handleWhatsApp}
                  disabled={!primaryValue}
                  variant="outlined"
                  startIcon={<WhatsAppIcon />}
                  fullWidth
                  sx={{
                    borderRadius: 3,
                    py: 1.05,
                    fontWeight: 900,
                    borderColor: 'rgba(34,197,94,0.35)',
                    color: 'rgba(21,128,61,1)',
                    background: 'linear-gradient(180deg, rgba(34,197,94,0.10) 0%, rgba(34,197,94,0.04) 100%)',
                    '&:hover': {
                      borderColor: 'rgba(34,197,94,0.55)',
                      background: 'linear-gradient(180deg, rgba(34,197,94,0.14) 0%, rgba(34,197,94,0.06) 100%)',
                    },
                  }}
                >
                  WhatsApp
                </Button>

                <Button
                  onClick={handleGmail}
                  disabled={!primaryValue}
                  variant="outlined"
                  startIcon={<MailOutlineIcon />}
                  fullWidth
                  sx={{ borderRadius: 3, py: 1.05, fontWeight: 900 }}
                >
                  Gmail
                </Button>
              </Stack>

              <Stack direction={isMobile ? 'column' : 'row'} spacing={1} sx={{ mt: 1 }}>
                <Button
                  onClick={handleFacebook}
                  disabled={!linkValue}
                  variant="outlined"
                  startIcon={<FacebookIcon />}
                  fullWidth
                  sx={{ borderRadius: 3, py: 1.05, fontWeight: 900 }}
                >
                  Facebook
                </Button>

                <Button
                  onClick={handleInstagram}
                  disabled={!primaryValue}
                  variant="outlined"
                  startIcon={<InstagramIcon />}
                  fullWidth
                  sx={{ borderRadius: 3, py: 1.05, fontWeight: 900 }}
                >
                  Instagram
                </Button>

                <Button
                  onClick={handleTikTok}
                  disabled={!primaryValue}
                  variant="outlined"
                  startIcon={<TikTokIcon />}
                  fullWidth
                  sx={{ borderRadius: 3, py: 1.05, fontWeight: 900 }}
                >
                  TikTok
                </Button>
              </Stack>
            </Box>

            {canNativeShare ? (
              <Button
                onClick={handleNativeShare}
                variant="text"
                startIcon={<IosShareIcon />}
                fullWidth
                sx={{ mt: 1.25, borderRadius: 3, py: 1, fontWeight: 900 }}
              >
                More share options
              </Button>
            ) : null}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ShareDialog;
